import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Storefront, Trophy, CalendarDots, Medal, Crown, Plus, Users, Star } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import ShareButton from '../components/ShareButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopProfilePage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [events, setEvents] = useState([]);
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('events');
  const [loading, setLoading] = useState(true);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ name: '', game_name: 'Catan', date: '', time: '18:00', max_players: 16, description: '' });

  useEffect(() => { fetchAll(); }, [shopId]);

  const fetchAll = async () => {
    try {
      const [shopRes, eventsRes, badgesRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/shops/${shopId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/shops/${shopId}/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/shops/${shopId}/badges`, { withCredentials: true }),
        axios.get(`${API_URL}/api/shops/${shopId}/leaderboard`, { withCredentials: true }),
      ]);
      setShop(shopRes.data);
      setEvents(eventsRes.data);
      setBadges(badgesRes.data);
      setLeaderboard(lbRes.data);
    } catch (err) { toast.error('Failed to load shop'); }
    finally { setLoading(false); }
  };

  const createEvent = async () => {
    try {
      await axios.post(`${API_URL}/api/shops/${shopId}/events`, eventForm, { withCredentials: true });
      toast.success('Event created!');
      setShowNewEvent(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create event'); }
  };

  const joinEvent = async (eventId) => {
    try {
      await axios.post(`${API_URL}/api/shop-events/${eventId}/join`, {}, { withCredentials: true });
      toast.success('Joined event!');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };

  const isOwner = shop?.owner_user_id === user?.id;

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="app-container" data-testid="shop-profile-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white truncate flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Storefront size={22} weight="duotone" className="text-[#00F0FF]" />
              {shop?.name}
              {shop?.verified && <Star size={14} weight="fill" className="text-[#FACC15]" />}
            </h1>
            <p className="text-[#71717A] text-xs truncate">{shop?.location}</p>
          </div>
          <ShareButton type="session" id={shopId} title="" />
        </div>

        {shop?.champion_name && (
          <div className="p-2 rounded-lg bg-[#FACC15]/10 border border-[#FACC15]/20 flex items-center gap-2 mb-3">
            <Crown size={16} weight="fill" className="text-[#FACC15]" />
            <span className="text-[#FACC15] text-xs font-medium">Shop Champion: {shop.champion_name}</span>
          </div>
        )}

        <div className="flex items-center gap-3 text-[10px] text-[#71717A] mb-3">
          <span className="flex items-center gap-1"><Users size={12} /> {shop?.member_count || 0} members</span>
          <span className={`px-2 py-0.5 rounded-full ${shop?.subscription_tier === 'paid' ? 'bg-[#FACC15]/10 text-[#FACC15]' : 'bg-[#181824] text-[#71717A]'}`}>
            {shop?.subscription_tier || 'free'}
          </span>
        </div>

        <div className="flex gap-2">
          {['events', 'badges', 'leaderboard'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32 space-y-3">
        {activeTab === 'events' && (
          <>
            {isOwner && (
              <button onClick={() => setShowNewEvent(!showNewEvent)} className="w-full p-3 rounded-xl bg-[#9D4CDD]/10 border border-[#9D4CDD]/30 text-[#9D4CDD] text-sm font-medium flex items-center justify-center gap-2" data-testid="create-shop-event-btn">
                <Plus size={16} weight="bold" /> Create Event
              </button>
            )}

            {showNewEvent && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="p-4 rounded-xl bg-[#0F0F16] border border-white/5 space-y-2">
                <input value={eventForm.name} onChange={e => setEventForm(f => ({ ...f, name: e.target.value }))} placeholder="Event Name" className="input-field text-sm" data-testid="event-name-input" />
                <input value={eventForm.game_name} onChange={e => setEventForm(f => ({ ...f, game_name: e.target.value }))} placeholder="Game" className="input-field text-sm" />
                <input type="date" value={eventForm.date} onChange={e => setEventForm(f => ({ ...f, date: e.target.value }))} className="input-field text-sm" />
                <textarea value={eventForm.description} onChange={e => setEventForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="input-field text-sm" rows={2} />
                <button onClick={createEvent} className="btn-primary w-full text-sm">Create</button>
              </motion.div>
            )}

            {events.length > 0 ? events.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                <div className="flex justify-between mb-1">
                  <h3 className="text-white text-sm font-medium">{e.name}</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF]">{e.game_name}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-[#71717A] mb-2">
                  <span><CalendarDots size={10} className="inline mr-1" />{e.date}</span>
                  <span>{e.current_players}/{e.max_players} joined</span>
                </div>
                {e.description && <p className="text-[#A1A1AA] text-[10px] mb-2">{e.description}</p>}
                <button onClick={() => joinEvent(e.id)} className="text-[#39FF14] text-xs font-medium" data-testid={`join-event-${e.id}`}>Join Event</button>
              </motion.div>
            )) : (
              <p className="text-center text-[#71717A] py-6 text-sm">No events yet</p>
            )}
          </>
        )}

        {activeTab === 'badges' && (
          badges.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {badges.map((b, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 text-center">
                  <Medal size={24} weight="duotone" className="text-[#FACC15] mx-auto mb-1" />
                  <p className="text-white text-xs font-medium">{b.name}</p>
                  {b.description && <p className="text-[#71717A] text-[10px]">{b.description}</p>}
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#71717A] py-6 text-sm">No badges created yet</p>
          )
        )}

        {activeTab === 'leaderboard' && (
          leaderboard.length > 0 ? leaderboard.map((p, i) => (
            <div key={p.id || i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center gap-3">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? 'bg-[#FACC15] text-[#0A0A12]' : 'bg-[#181824] text-[#71717A]'}`}>
                {i + 1}
              </span>
              <div className="flex-1">
                <p className="text-white text-xs font-medium">{p.name}</p>
                <p className="text-[#71717A] text-[10px]">Rating: {p.rating}</p>
              </div>
              <span className="text-[#39FF14] text-xs font-bold">{p.wins}W</span>
            </div>
          )) : (
            <p className="text-center text-[#71717A] py-6 text-sm">No rankings yet</p>
          )
        )}
      </div>

      <BottomNav />
    </div>
  );
}
